import React, { useCallback, useRef } from 'react';
import ModalItem from '../../common/ModalItem/ModalItem';
import { Container, TextField } from '@material-ui/core';
import { Trans, useTranslation } from 'react-i18next';
import addImage from './addImage.svg';
import './task-finish.scss';
import { finishingSolution, finishingSolutionImages } from '../../../store/activitiesSlice';
import { useFormik } from 'formik';
import YellowButton from '../../common/YellowButton';
import { useDropzone } from 'react-dropzone';
import { changeErrorPopupState, changeSuccessPopupState } from '../../../store/uiSlice';
import { connect } from 'react-redux';
import CustomBackdrop from '../../common/CustomBackdrop';
import { useHistory, useLocation } from 'react-router';
import { completedRoutes } from '../../../common/constants';
import { get, uniqueId } from 'lodash';
import ClearIcon from '@material-ui/icons/Clear';
import { getTaskSolutionsCountById } from '../../../store/tasksSlice';
import { Link } from 'react-router-dom';

const maxFileSize = 10485760;
const maxFilesCount = 10;

function TaskFinish(props) {
  const { t } = useTranslation();
  const location = useLocation();
  const solutionId = get(location, 'state.solutionId', null);
  const taskId = get(location, 'state.taskId', null);
  const history = useHistory();

  const formik = useFormik({
    initialValues: {
      input1: '',
      files: [],
    },
    validate: (values) => {
      const errors = {};
      if (values.input1 === '') {
        errors.input1 = 'Required';
      }
      return errors;
    },
    onSubmit: (values, actions) => {
      const { input1, files } = values;
      const formData = new FormData();
      for (const file of files) {
        formData.append('solution_images[]', file)
      }
      props.finishingSolutionImages(solutionId, formData)
        .then(() => {
          return props.finishingSolution(solutionId, { input1 })
        })
        .then(() => {
          props.changeSuccessPopupState({ isOpen: true, type: 'taskComplete' });
        })
        .catch((error) => {
          if (error.status === 401) {
            history.replace(
              completedRoutes.signUpModalItem,
              { currentUrl: completedRoutes.taskFinish(taskId), solutionId }
            );
          } else if (error.status === 400) {
            props.changeErrorPopupState({ isOpen: true, error, type: 'taskComplete', data: { taskId } });
            props.getTaskSolutionsCountById(taskId);
          } else {
            console.error(error);
            props.changeErrorPopupState({ isOpen: true, error, type: 'default' });
          }
        })
        .finally(() => {
          actions.setSubmitting(false);
        })
    },
  });

  const { changeErrorPopupState } = props;

  const onDrop = useCallback((acceptedFiles) => {
    const filteredFiles = acceptedFiles.filter((file) => {
      const { size } = file;
      if (size > maxFileSize) {
        changeErrorPopupState({ type: 'fileSize', isOpen: true, data: { maxFileSize } });
        return false;
      }
      return true;
    });

    filteredFiles.map((file) => Object.assign(file, {
      preview: URL.createObjectURL(file),
      id: uniqueId(),
    }));

    const newFiles = [...formik.values.files, ...filteredFiles];
    if (newFiles.length > maxFilesCount) {
      changeErrorPopupState({ type: 'filesCount', isOpen: true, data: { maxFilesCount } });
    }
    formik.setFieldValue('files', newFiles.slice(0, maxFilesCount));
  }, [formik, changeErrorPopupState]);

  const handleRemove = (id) => () => {
    const restFiles = formik.values.files.filter((i) => i.id !== id);
    formik.setFieldValue('files', restFiles);
  };

  const dropAreaRef = useRef();
  const handleDisableFocus = () => {
    dropAreaRef.current.blur();
  };

  const isDropZoneDisabled = formik.values.files.length >= maxFilesCount;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*',
    multiple: true,
    disabled: isDropZoneDisabled,
  });

  if (solutionId === null) return null;

  const hint = get(props.tasksById[taskId], 'hint', undefined);
  if (hint === undefined) return null;
  const isFetching = formik.isSubmitting && !formik.isValidating;

  const thumbs = formik.values.files.map((file) => {
    const { preview, name, id } = file;
    return (
      <div key={`${name}-${id}`} className="image-item">
        <div>
          <span className="remove-btn" onClick={handleRemove(id)} role="button" tabIndex="0">
            <ClearIcon />
          </span>
          <img src={preview} alt="upload preview" />
        </div>
      </div>
    )
  });

  return (
    <ModalItem>
      <>
        <Container className="task-complete-container">
          <div className="content">
            <div dangerouslySetInnerHTML={{ __html: hint }} className="hint" />
            <form onSubmit={formik.handleSubmit}>
              <TextField
                fullWidth
                label={t('common.typeText')}
                name="input1"
                multiline
                className="text-input"
                value={formik.values.input1}
                onChange={formik.handleChange}
              />
              {!isDropZoneDisabled &&
              <div {...getRootProps()} onFocus={handleDisableFocus} ref={dropAreaRef}>
                <div className={`upload-image-btn ${isDragActive ? 'active' : ''}`}>
                  <img src={addImage} alt="add icon" />
                  <span className="btn-caption">
                      {
                        !isDragActive ? t('common.addImage') :
                          t('common.addImageDragActive')
                      }
                    </span>
                </div>
                <input {...getInputProps()} />
              </div>
              }
              <div className="images-preview">
                {thumbs}
              </div>
              <div className="rules-info">
                <Trans i18nKey="taskFinish.rulesInfo">
                  %<strong/><Link to={completedRoutes.userRules}/>%
                </Trans>
              </div>
              <p>{' '}<br /></p>
              <YellowButton
                type="submit"
                title={t('common.send')}
                hidden={false}
                disabled={formik.errors.input1 || formik.values.input1.length === 0}
              />
            </form>
          </div>
        </Container>
        <CustomBackdrop open={isFetching} />
      </>
    </ModalItem>
  );
}

const mapStateToProps = (state) => ({
  tasksById: state.tasks.data.byId,
  tasksFetchingState: state.tasks.fetchingState,
});

const actionCreators = {
  changeErrorPopupState: changeErrorPopupState,
  changeSuccessPopupState: changeSuccessPopupState,
  finishingSolution,
  finishingSolutionImages,
  getTaskSolutionsCountById,
};

export default connect(mapStateToProps, actionCreators)(TaskFinish);
